import { z } from 'zod';
/**
 * Canonical factory-scoped Mission Control fire terminal statuses.
 *
 * This is the static contract used by OCNexus factory polling/reaper code
 * when deciding that an MC fire is no longer in flight. The set is the union
 * of historical factory terminals plus launcher webhook terminal outcomes.
 */
export const MC_FIRE_TERMINAL_STATUSES = new Set([
    'completed',
    'completed_no_push',
    'completed_no_handoff',
    'completed_apply_blocked',
    'completed_with_scar',
    'completed_branch_only',
    'failed',
    'failed_review',
    'cancelled',
    'cancelled_by_signal',
    'expired',
    'blocked',
]);
/**
 * Factory-scoped MC fire statuses that should advance a factory child run as
 * successful. Most terminal statuses are failures or terminal-without-merge;
 * `completed_with_scar` is successful with a documented warning/scar.
 */
export const MC_FIRE_SUCCESS_STATUSES = new Set([
    'completed',
    'completed_with_scar', // success with warning; scar is preserved by MC handoff/metadata.
]);
export const factoryBuildStatuses = [
    'spec_generating',
    'spec_generated',
    'components_pending',
    'components_running',
    'components_complete',
    'deploy_pending',
    'deploying',
    'complete',
    'failed',
    'cancelled',
];
export const factoryChildRunStatuses = [
    'pending',
    'claimed',
    'running',
    'complete',
    'failed',
    'cancelled',
];
export const factoryFailureReasons = [
    'spec_failed',
    'component_failed',
    'deploy_failed',
    'lease_lost',
    'worker_failed',
    'cancelled',
    'unknown',
];
export const factoryBuildStatusSchema = z.enum(factoryBuildStatuses);
export const factoryChildRunStatusSchema = z.enum(factoryChildRunStatuses);
export const factoryFailureReasonSchema = z.enum(factoryFailureReasons);
export const factoryBuildStatePatchSchema = z.object({
    status: factoryBuildStatusSchema.optional(),
    error: z.record(z.unknown()).nullable().optional(),
    deployment: z.record(z.unknown()).nullable().optional(),
    completed_at: z.coerce.date().nullable().optional(),
});
export const factoryChildRunPatchSchema = z.object({
    status: factoryChildRunStatusSchema.optional(),
    phase: z.string().min(1).max(128).nullable().optional(),
    progress_percent: z.number().min(0).max(100).optional(),
    failure_reason: factoryFailureReasonSchema.nullable().optional(),
    error_message: z.string().max(4_000).nullable().optional(),
});
export const factoryTransitionEventSchema = z.object({
    factory_build_id: z.string().min(1),
    child_run_id: z.string().min(1).optional(),
    previous_status: factoryBuildStatusSchema.optional(),
    next_status: factoryBuildStatusSchema,
    reason: z.string().min(1).max(512).optional(),
    metadata: z.record(z.unknown()).optional(),
    emitted_at: z.coerce.date().default(() => new Date()),
});
export function parseFactoryBuildStatePatch(input) {
    return factoryBuildStatePatchSchema.parse(input);
}
export function parseFactoryChildRunPatch(input) {
    return factoryChildRunPatchSchema.parse(input);
}
export function parseFactoryTransitionEvent(input) {
    return factoryTransitionEventSchema.parse(input);
}
//# sourceMappingURL=factory-state.js.map